import { Button, Popover, PopoverContent, PopoverTrigger } from '@heroui/react';
import { Ban } from 'lucide-react';
import React, { FC } from 'react';

import { useAdGeneratorSettings } from '../../../../queries';
import { ColorButton, ColorPreview, FieldLabel, FieldRow, PaletteLabel, SwatchButton, SwatchGrid } from './Controls.styled';

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
  /** Pozwala wyczyścić kolor, np. przy obrysie albo tle plakietki. */
  allowEmpty?: boolean;
}

/**
 * Próbnik pokazuje kolory marki i zapisane palety z ustawień generatora, a nie
 * listę zaszytą w kodzie — dzięki temu kreacje trzymają się jednej identyfikacji
 * wizualnej, którą marketing zmienia w jednym miejscu.
 */
export const ColorField: FC<ColorFieldProps> = ({ label, value, onChange, allowEmpty }) => {
  const { brandColors, palettes } = useAdGeneratorSettings();

  return (
    <FieldRow>
      <FieldLabel>{label}</FieldLabel>
      <Popover placement="left" showArrow>
        <PopoverTrigger>
          <ColorButton type="button" aria-label={`Zmień kolor: ${label}`}>
            <ColorPreview $color={value} $empty={!value} />
            <span>{value ? value.toUpperCase() : 'brak'}</span>
          </ColorButton>
        </PopoverTrigger>
        <PopoverContent className="p-3">
          <PaletteLabel>Kolory marki</PaletteLabel>
          <SwatchGrid>
            {brandColors.map((swatch) => (
              <SwatchButton
                key={swatch}
                type="button"
                $color={swatch}
                $active={swatch.toLowerCase() === value.toLowerCase()}
                aria-label={swatch}
                onClick={() => onChange(swatch)}
              />
            ))}
          </SwatchGrid>

          {palettes.map((palette) => (
            <div key={palette.id} className="mt-3">
              <PaletteLabel>{palette.name}</PaletteLabel>
              <SwatchGrid>
                {palette.colors.map((swatch) => (
                  <SwatchButton
                    key={`${palette.id}-${swatch}`}
                    type="button"
                    $color={swatch}
                    $active={swatch.toLowerCase() === value.toLowerCase()}
                    aria-label={swatch}
                    onClick={() => onChange(swatch)}
                  />
                ))}
              </SwatchGrid>
            </div>
          ))}
          <div className="mt-3 flex items-center gap-2">
            <input
              type="color"
              value={value || '#000000'}
              onChange={(event) => onChange(event.target.value)}
              aria-label="Wybierz własny kolor"
              className="h-8 w-10 cursor-pointer rounded border border-gray-200 bg-white p-0"
            />
            {allowEmpty && (
            <Button size="sm" variant="light" radius="sm" startContent={<Ban size={14} />} onPress={() => onChange('')}>
              Bez koloru
            </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </FieldRow>
  );
};
